import { useRef, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import { date, money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import {
  PAID_FROM_LABELS,
  STATUS_LABELS,
  downloadExpenseReceipt,
  useConfirmExpense,
  useCreateExpense,
  useExpenses,
  useReopenExpense,
  useUploadExpenseReceipt,
  type Expense,
  type ExpenseStatus,
  type PaidFrom,
} from '../lib/expenses-api';
import { usePostDocument } from '../lib/accounting-api';
import { saveBlob } from '../lib/einvoice-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * GİDERLER — resmî faturası olmayan küçük harcamalar.
 *
 * ⚠ BU EKRANDA KDV İNDİRİMİ SORULMUYOR ve bu bir eksiklik değil, hükmün
 * kendisidir. AR n° 3 md. 3 §1, 1° indirim hakkını kullanabilmek için usulüne
 * uygun bir FATURA elde tutulmasını şart koşuyor; fiş bunu sağlamaz. Alanı
 * gösterip "boş bırakın" demek yerine HİÇ göstermiyoruz — sunucuda kolon da
 * yok, yani soru hiçbir katmanda sorulamıyor.
 *
 * ⚠ AYRIM BELGENİN TÜRÜDÜR, TUTARIN BÜYÜKLÜĞÜ DEĞİL. 3 €'luk bir otopark fişi
 * buraya, 3 €'luk bir hosting FATURASI "Alış faturaları"na girer. Ekranın
 * açıklaması bunu her açılışta söylüyor, çünkü yanlış ekrana giren bir fatura
 * KDV indirimini SESSİZCE kaybettirir.
 */

const PAGE_SIZE = 25;

type StatusFilter = ExpenseStatus | 'all';
type AttachmentFilter = 'all' | 'missing' | 'present';

export function ExpensesPage() {
  const { user } = useAuth();
  const readOnly = isReadOnly(user);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [attachment, setAttachment] = useState<AttachmentFilter>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);

  const list = useExpenses({
    page,
    pageSize: PAGE_SIZE,
    status: status === 'all' ? undefined : status,
    missingAttachment: attachment === 'all' ? undefined : attachment === 'missing',
    q: q.trim() || undefined,
  });

  return (
    <>
      <PageHeader
        title="Giderler"
        description="Faturası olmayan küçük harcamalar: otopark, kasa fişi, harç. Faturası OLAN her alım 'Alış faturaları' ekranına girer — KDV indirimi yalnız orada vardır."
        actions={
          readOnly ? null : (
            <Button onClick={() => setOpen((v) => !v)}>{open ? 'Vazgeç' : 'Yeni gider'}</Button>
          )
        }
      />

      {open && !readOnly && <ExpenseForm onDone={() => setOpen(false)} />}

      <Card className="mt-4">
        <CardHeader
          title="Kayıtlar"
          description="KDV sütunu yok: fişten KDV indirilemez (AR n° 3 md. 3 §1, 1° usulüne uygun FATURA istiyor)."
        />
        <div className="flex flex-wrap gap-2 px-4 py-3">
          <Select
            label="Durum"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StatusFilter);
              setPage(1);
            }}
          >
            <option value="all">Hepsi</option>
            <option value="DRAFT">Taslak</option>
            <option value="CONFIRMED">Onaylandı</option>
          </Select>
          {/*
            ⚠ "FİŞ GÖRÜNTÜSÜ" SÜZGECİ BİR TELAFİDİR. Fişsiz onaylamak BLOKE
            EDİLMİYOR (kaybolmuş bir fişi zorunlu tutmak, gideri hiç
            kaydetmemeye ya da rastgele bir dosya yüklemeye iterdi) — ama
            eksiz kayıtlar sessizce birikmemeli.
          */}
          <Select
            label="Fiş görüntüsü"
            value={attachment}
            onChange={(e) => {
              setAttachment(e.target.value as AttachmentFilter);
              setPage(1);
            }}
          >
            <option value="all">Hepsi</option>
            <option value="missing">Yalnız eksikler</option>
            <option value="present">Yalnız yüklenmişler</option>
          </Select>
          <Input
            label="Ara"
            value={q}
            placeholder="Açıklama"
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {list.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : list.isError ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Kayıt yok" description="Henüz gider girilmemiş." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Tarih</Th>
                <Th>Açıklama</Th>
                <Th align="right">Tutar (KDV dâhil)</Th>
                <Th align="right">Gelir vergisi</Th>
                <Th>Ödeme</Th>
                <Th>Fiş</Th>
                <Th>Durum</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((e) => (
                <ExpenseRow key={e.id} expense={e} readOnly={readOnly} />
              ))}
            </tbody>
          </Table>
        )}

        {(list.data?.total ?? 0) > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 text-xs text-[var(--c-ink-soft)]">
            <span>
              {list.data?.total} kayıt · sayfa {page}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                Önceki
              </Button>
              <Button
                variant="ghost"
                disabled={page * PAGE_SIZE >= (list.data?.total ?? 0)}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

function ExpenseRow({ expense, readOnly }: { expense: Expense; readOnly: boolean }) {
  const confirm = useConfirmExpense();
  const reopen = useReopenExpense();
  const upload = useUploadExpenseReceipt();
  const postToLedger = usePostDocument();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const pickFile = (): void => fileInput.current?.click();

  // Salt okuma rolü de indirebilir — indirmek bir yazma değildir.
  const download = (): void => {
    setError(null);
    setDownloading(true);
    void downloadExpenseReceipt(expense.id)
      .then((file) => saveBlob(file.blob, file.fileName ?? `gider-${expense.id}`))
      .catch((e: unknown) => setError(errorMessage(e)))
      .finally(() => setDownloading(false));
  };

  const onFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync({ id: expense.id, file });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <>
      <tr>
        <Td>{date(expense.expenseDate)}</Td>
        <Td>{expense.description}</Td>
        <Td align="right">{money(expense.amountInclVat)}</Td>
        {/*
          ⚠ TEK İNDİRİM EKSENİ GELİR VERGİSİDİR. Sütun başlığı bunu söylüyor;
          "indirilebilir" demek, KDV sanılmasına açık kapı bırakırdı.
        */}
        <Td align="right">%{expense.incomeTaxDeductiblePct}</Td>
        <Td>{PAID_FROM_LABELS[expense.paidFrom]}</Td>
        <Td>
          {expense.attachmentStoragePath ? (
            <div className="flex items-center gap-1.5">
              <Badge tone="ok">var</Badge>
              <Button variant="ghost" size="sm" disabled={downloading} onClick={download}>
                indir
              </Button>
            </div>
          ) : (
            <Badge tone="warn">yok</Badge>
          )}
        </Td>
        <Td>
          <Badge tone={expense.status === 'CONFIRMED' ? 'ok' : 'neutral'}>
            {STATUS_LABELS[expense.status]}
          </Badge>
        </Td>
        <Td>
          {!readOnly && (
            <div className="flex gap-1.5">
              {!expense.attachmentStoragePath && (
                <>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    className="hidden"
                    onChange={(ev) => void onFile(ev.target.files?.[0])}
                  />
                  <Button variant="ghost" onClick={pickFile} disabled={upload.isPending}>
                    Fiş yükle
                  </Button>
                </>
              )}
              {/*
                ⚠ KAYIT AYRI BİR ADIM. Gider onaylandığında deftere OTOMATİK
                düşmez: sunucuda kayıt, belge işleminin transaction'ı dışında
                tutuluyor (sayaç kilidi + deadlock riski). Düğme burada, çünkü
                operatör belgeyi burada görüyor.
              */}
              {expense.status === 'CONFIRMED' && (
                <Button
                  variant="ghost"
                  disabled={postToLedger.isPending}
                  onClick={() =>
                    void act(() =>
                      postToLedger.mutateAsync({ sourceType: 'EXPENSE', sourceId: expense.id }),
                    )
                  }
                >
                  Deftere kaydet
                </Button>
              )}
              {expense.status === 'DRAFT' ? (
                <Button
                  variant="ghost"
                  disabled={confirm.isPending}
                  onClick={() => void act(() => confirm.mutateAsync(expense.id))}
                >
                  Onayla
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  disabled={reopen.isPending}
                  onClick={() => void act(() => reopen.mutateAsync(expense.id))}
                >
                  Taslağa aç
                </Button>
              )}
            </div>
          )}
        </Td>
      </tr>
      {error && (
        <tr>
          <Td colSpan={8}>
            <p className="text-xs text-[var(--c-danger)]">{error}</p>
          </Td>
        </tr>
      )}
    </>
  );
}

function ExpenseForm({ onDone }: { onDone: () => void }) {
  const create = useCreateExpense();

  const [expenseDate, setExpenseDate] = useState('');
  const [description, setDescription] = useState('');
  const [amountInclVat, setAmount] = useState('');
  const [incomeTaxDeductiblePct, setPct] = useState('100');
  const [paidFrom, setPaidFrom] = useState<PaidFrom>('PRIVATE');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await create.mutateAsync({
        expenseDate,
        description,
        amountInclVat,
        incomeTaxDeductiblePct,
        paidFrom,
        ...(notes.trim() ? { notes } : {}),
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader
        title="Yeni gider"
        description="Tutar KDV DÂHİL girilir; fişte matrah/KDV ayrımı çoğu zaman yoktur ve ayırmak olmayan bir bilgiyi uydurmak olurdu."
      />
      <div className="grid gap-3 px-4 py-3 sm:grid-cols-2">
        <Input
          label="Tarih"
          type="date"
          value={expenseDate}
          onChange={(e) => setExpenseDate(e.target.value)}
        />
        <Input
          label="Açıklama"
          value={description}
          placeholder="Otopark — müşteri ziyareti"
          onChange={(e) => setDescription(e.target.value)}
        />
        <Input
          label="Tutar (KDV dâhil)"
          value={amountInclVat}
          placeholder="4.50"
          hint="Nokta ile: 4.50"
          onChange={(e) => setAmount(e.target.value)}
        />
        {/*
          ⚠ TEK ORAN ALANI VE O DA KDV DEĞİL. Alış ekranı iki ekseni ayrı
          sorar; burada KDV ekseni HİÇ YOK, çünkü fiş indirim hakkı vermiyor.
        */}
        <Input
          label="Gelir vergisinden düşülen oran (%)"
          value={incomeTaxDeductiblePct}
          hint="KDV oranı DEĞİL — fişten KDV indirilemez."
          onChange={(e) => setPct(e.target.value)}
        />
        <Select
          label="Ödeme"
          value={paidFrom}
          onChange={(e) => setPaidFrom(e.target.value as PaidFrom)}
        >
          {(Object.keys(PAID_FROM_LABELS) as PaidFrom[]).map((k) => (
            <option key={k} value={k}>
              {PAID_FROM_LABELS[k]}
            </option>
          ))}
        </Select>
        <Input label="Not" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      {error && <p className="px-4 pb-2 text-xs text-[var(--c-danger)]">{error}</p>}

      <div className="flex justify-end gap-2 border-t border-[var(--c-border)] px-4 py-3">
        <Button variant="ghost" onClick={onDone}>
          Vazgeç
        </Button>
        <Button disabled={create.isPending} onClick={() => void submit()}>
          Kaydet
        </Button>
      </div>
    </Card>
  );
}
